import cx from "classnames"
import { navigate } from "gatsby"
import PropTypes from "prop-types"
import React from "react"
import Link from "components/Link"
import { hrefIsRelative } from "lib/functions"
import { trackEvent } from "lib/googleTagManager"

/** Regular buttons, but with support for Gatsby navigation */
function Button({
  href,
  onClick,
  children,
  className,
  target,
  rel,
  trackClick,
  link,
  color,
  outline,
  shadow,
  size,
  dark,
  nowrap,
  ...rest
}) {
  const classNames = cx(
    "clickable font-barlow inline-flex rounded-1x font-bold transition-button",
    {
      /* Sizes */
      "text-base": size === "small",
      "text-xl": size === "medium",
      "text-base xl:text-xl": size === "flexible",
      /* Spacing */
      "px-5 py-2": !link && size === "small",
      "px-5 py-3": !link && size === "medium",
      "px-5 py-2 xl:py-3": !link && size === "flexible",
      /* Text color */
      "text-lush": link && color === "lush",
      "text-white": link && color === "white",
      /* Background color */
      "bg-lush text-white hover:bg-gossamer hover:text-white":
        !link && !outline && (color === "lush" || color === "green"),
      "bg-catskill-white text-cove hover:bg-black-squeeze hover:text-cove":
        !link && !outline && (color === "catskill-white" || color === "white"),
      /* Border color */
      "border border-ocean text-whisper-grey hover:text-white hover:bg-ocean":
        !!outline && color === "ocean",
      /* Shadow */
      "shadow-1x active:shadow-none": !!shadow,
      /* No wrap */
      "whitespace-nowrap": !!nowrap,
    },
    className
  )

  const handleClick = (e) => {
    /** Used to navigate to a local Gatsby page */
    if (href && hrefIsRelative(href)) {
      navigate(href)
      e.preventDefault()
    }

    /** If the trackClick is not empty, we trigger the buttonClick event */
    if (trackClick) {
      trackEvent("buttonClick", { buttonClickEventName: trackClick })
    }

    if (onClick) {
      onClick(e)
    }
  }

  const ButtonComponent = href ? (hrefIsRelative(href) ? Link : "a") : "button"

  return (
    <ButtonComponent
      href={href}
      onClick={handleClick}
      className={classNames}
      rel={rel || (target === "_blank" ? "noopener" : null)}
      target={target}
      {...rest}
    >
      {children}
    </ButtonComponent>
  )
}

Button.propTypes = {
  /** Local path to link to */
  href: PropTypes.string,
  /** onClick callback */
  onClick: PropTypes.func,
  /** Button size */
  size: PropTypes.oneOf(["small", "medium", "flexible"]),
  /** Button color */
  color: PropTypes.oneOf(["catskill-white", "ocean", "lush", "white", "green"]),
  /** If true, color is applied to border */
  outline: PropTypes.bool,
  /** If true, add shadow */
  shadow: PropTypes.bool,
  /** If true, adapt styles for dark background */
  dark: PropTypes.bool,
  /** If true, don't break lines */
  nowrap: PropTypes.bool,
  /** Use link styling - similar to `text` but color is set to the link color */
  link: PropTypes.bool,
  /** Additional className(s) */
  className: PropTypes.string,
  /** Link target property */
  target: PropTypes.string,
  /** Link rel property */
  rel: PropTypes.string,
  /** Track click event - if set, will track click */
  trackClick: PropTypes.string,
  /** Children */
  children: PropTypes.node,
}

export default Button
